// Standalone informed-consent signing — /informed-consent?t=<link token>
//
// Independent of the questionnaire: no Zustand store, no patient session, its
// own consent session (X-Consent-Token). The link can be re-opened until the
// document is signed; afterwards it only shows the signed result.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CheckCircle2, ShieldCheck, FileText, ExternalLink, Download, AlertTriangle, Loader2 } from 'lucide-react'
import { Logo } from '../components/Logo'
import { StepIndicator } from '../components/StepIndicator'
import { Button } from '../components/Button'
import { useTenant } from '../context/TenantContext'
import { consentService, ConsentRequestSummary, getApiErrorCode, getApiErrorMessage, getApiErrorDetails } from '../services/api'
import { logger } from '../utils/logger'
import { LegalFooter } from '../components/LegalFooter'

type Phase = 'loading' | 'invalid' | 'expired' | 'canceled' | 'read' | 'otp' | 'signed' | 'error'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_S = 30

export function InformedConsentPage() {
  const { t, i18n } = useTranslation()
  // The exchange must carry X-Tenant-ID: without it the API defaults to the
  // German org and an Italian link would be rejected. Wait for the tenant.
  const { tenant, loading: tenantLoading } = useTenant()

  const [phase, setPhase] = useState<Phase>('loading')
  const [session, setSession] = useState<string | null>(null)
  const [request, setRequest] = useState<ConsentRequestSummary | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  // true once the blob in pdfUrl is the SIGNED copy (not the pre-signature preview)
  const [signedPdfReady, setSignedPdfReady] = useState(false)
  // polling gave up (e.g. Youtrust throttled the download): tell the patient the copy follows
  const [copyFollowsLater, setCopyFollowsLater] = useState(false)
  const [consents, setConsents] = useState<Record<string, boolean>>({ read_understood: false, privacy: false })
  const [busy, setBusy] = useState(false)
  const [code, setCode] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [phoneMasked, setPhoneMasked] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  const linkToken = new URLSearchParams(window.location.search).get('t') || ''
  // Opened from the Bezugspersonenbogen: this page is step 2 of 2
  const fromRelatedPersonForm = new URLSearchParams(window.location.search).get('flow') === 'bezugsperson'
  // Mobile browsers (Android Chrome, iOS Safari) do not render PDFs inside an
  // iframe reliably — offer the document as a button that opens it instead.
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

  // Load the (prefilled or signed) PDF for the preview + download
  const loadPdf = useCallback(async (sess: string) => {
    setPdfLoading(true)
    try {
      const blob = await consentService.document(sess)
      // Revoke the previous blob only after a grace period: a download or a new tab
      // started a moment earlier must not end up with an empty file.
      setPdfUrl(prev => { if (prev) setTimeout(() => URL.revokeObjectURL(prev), 60_000); return URL.createObjectURL(blob) })
      return true
    } catch (err) {
      logger.warn('[Consent] document not available yet:', getApiErrorCode(err))
      return false
    } finally {
      setPdfLoading(false)
    }
  }, [])

  // Exchange the link for a session
  useEffect(() => {
    if (tenantLoading) return
    if (!linkToken) { setPhase('invalid'); return }
    let cancelled = false
    ;(async () => {
      try {
        const { sessionToken, request: req } = await consentService.exchange(linkToken)
        if (cancelled) return
        if (req.locale && req.locale !== i18n.language) i18n.changeLanguage(req.locale)
        setSession(sessionToken)
        setRequest(req)
        // Start from what was ticked on an earlier visit — otherwise the preview shows a box
        // the page thinks is empty and the patient cannot untick it (Tessera Sanitaria complaints)
        if (req.consents.length > 0) setConsents(Object.fromEntries(req.consents.map(c => [c.key, !!req.given?.[c.key]])))
        setPhase(['signed', 'stored'].includes(req.status) ? 'signed' : 'read')
        loadPdf(sessionToken).then(ok => { if (ok && req.stored) setSignedPdfReady(true) })
      } catch (err) {
        if (cancelled) return
        const code = getApiErrorCode(err)
        // Explain it in the language the link was sent in
        const linkLocale = getApiErrorDetails(err)?.locale
        if (typeof linkLocale === 'string' && linkLocale !== i18n.language) i18n.changeLanguage(linkLocale)
        if (code === 'consent_link_expired') setPhase('expired')
        else if (code === 'consent_canceled') setPhase('canceled')
        else setPhase('invalid')
      }
    })()
    return () => { cancelled = true }
  }, [tenantLoading, linkToken, loadPdf, i18n])

  // After signing: poll until the signed copy is stored, then reload the PDF
  useEffect(() => {
    if (phase !== 'signed' || !session || request?.stored) return
    let tries = 0
    const id = setInterval(async () => {
      tries += 1
      try {
        const me = await consentService.me(session)
        setRequest(me)
        if (me.stored) { clearInterval(id); loadPdf(session).then(ok => { if (ok) setSignedPdfReady(true) }) }
      } catch { /* keep polling */ }
      if (tries >= 20) { clearInterval(id); setCopyFollowsLater(true) }
    }, 3000)
    return () => clearInterval(id)
  }, [phase, session, request?.stored, loadPdf])

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown(c => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  const handleAuthError = (err: unknown): boolean => {
    const code = getApiErrorCode(err)
    if (code === 'signing_busy') {
      // Youtrust's API budget for the practice is momentarily exhausted — say how long, keep the entered state
      const retryAfter = Number((err as { response?: { headers?: Record<string, string> } })?.response?.headers?.['retry-after'])
      const minutes = Math.max(1, Math.ceil((Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 120) / 60))
      toast.error(t('informedConsent.busy', { minutes }), { duration: 10000 })
      return true
    }
    if (code === 'consent_session_expired' || code === 'consent_session_required') {
      // Session ran out while reading — the link is still valid, just reload it
      window.location.reload()
      return true
    }
    if (code === 'consent_link_expired') { setPhase('expired'); return true }
    if (code === 'consent_canceled') { setPhase('canceled'); return true }
    if (code === 'already_signed') { setPhase('signed'); return true }
    return false
  }

  const sendOtp = async (resend = false) => {
    if (!session) return
    setBusy(true)
    try {
      // Send EVERY document consent explicitly (false included): a key left out would keep an
      // older 'true' on the server
      const payload = docConsents.length > 0 ? Object.fromEntries(docConsents.map(c => [c.key, !!consents[c.key]])) : consents
      const r = await consentService.sendOtp(session, payload)
      setPhoneMasked(r.phoneMasked)
      setCooldown(RESEND_COOLDOWN_S)
      setCode(Array(OTP_LENGTH).fill(''))
      setPhase('otp')
      toast.success(resend ? t('informedConsent.newCodeSent') : t('informedConsent.codeSent'))
      setTimeout(() => otpRefs.current[0]?.focus(), 50)
    } catch (err) {
      if (handleAuthError(err)) return
      const code = getApiErrorCode(err)
      if (code === 'consents_required') toast.error(t('informedConsent.consentsRequired'))
      else if (code === 'purpose_required') toast.error(t('informedConsent.waiver.purposeRequired'))
      else if (code === 'otp_rate_limited') toast.error(t('common.tooManyOtpRequests'))
      else if (code === 'sr_cap_reached') toast.error(t('informedConsent.capReached'))
      else if (code === 'phone_invalid') toast.error(t('informedConsent.phoneInvalid'), { duration: 12000 })
      else if (code === 'sr_create_failed' || code === 'yousign_not_configured' || code === 'sms_failed' || code === 'sms_not_configured') toast.error(t('informedConsent.prepareFailed'))
      else toast.error(getApiErrorMessage(err) || t('common.errorSendingCode'))
      logger.error('[Consent] send-otp failed:', err)
    } finally {
      setBusy(false)
    }
  }

  const sign = async () => {
    if (!session) return
    const otp = code.join('')
    if (otp.length !== OTP_LENGTH) return
    setBusy(true)
    try {
      await consentService.sign(session, otp)
      setPhase('signed')
      setRequest(r => r ? { ...r, status: 'signed', signedAt: new Date().toISOString() } : r)
      toast.success(t('informedConsent.signedToast'))
    } catch (err) {
      if (handleAuthError(err)) return
      const c = getApiErrorCode(err)
      if (c === 'otp_invalid' || c === 'otp_rejected') toast.error(t('common.invalidCodeRetry'))
      // otp_not_sent: the code was spent or withdrawn (e.g. the practice changed the number) — ask for a new one
      else if (c === 'otp_expired' || c === 'otp_not_sent') toast.error(t('informedConsent.codeExpired'))
      else if (c === 'otp_rate_limited') toast.error(t('common.tooManyAttempts'))
      else if (c === 'ip_detection_failed') toast.error(t('informedConsent.ipFailed'))
      else toast.error(getApiErrorMessage(err) || t('common.serverError'))
      setCode(Array(OTP_LENGTH).fill(''))
      otpRefs.current[0]?.focus()
    } finally {
      setBusy(false)
    }
  }

  // OTP input helpers (same UX as the questionnaire's step 10)
  const onOtpChange = (i: number, v: string) => {
    const d = v.replace(/\D/g, '').slice(-1)
    setCode(prev => { const n = [...prev]; n[i] = d; return n })
    if (d && i < OTP_LENGTH - 1) otpRefs.current[i + 1]?.focus()
  }
  const onOtpKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) otpRefs.current[i - 1]?.focus()
  }
  const onOtpPaste = (e: React.ClipboardEvent) => {
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH).split('')
    if (digits.length === 0) return
    e.preventDefault()
    setCode(Array.from({ length: OTP_LENGTH }, (_, i) => digits[i] || ''))
    otpRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus()
  }

  const isWaiver = !!request?.kind?.startsWith('confidentiality_waiver')

  const download = () => {
    if (!pdfUrl || !request) return
    // Android/iOS: the download attribute on a blob URL is unreliable (0-byte files);
    // opening the blob in a new tab works everywhere and offers "save" from there.
    if (isMobile) { window.open(`${pdfUrl}#navpanes=0&pagemode=none`, '_blank', 'noopener'); return }
    const a = document.createElement('a')
    a.href = pdfUrl
    a.download = `${isWaiver ? 'schweigepflichtentbindung' : 'informed-consent'}-${request.patient.patientNumber}${request.stored ? '-signed' : ''}.pdf`
    a.click()
  }

  const kindTitle = request ? t(`informedConsent.kind.${request.kind}`) : ''
  // Consents come from the document itself when the template defines them; otherwise the default pair
  const docConsents = request?.consents ?? []
  // Schweigepflichtentbindung: every purpose is a choice; at least one must be released.
  // Nothing is pre-ticked — what the clinician asked for is only marked as "requested".
  const modeKeys = request?.modeKeys ?? []
  const requested = new Set(request?.requestedPurposes ?? [])
  const purposeGiven = docConsents.some(c => !modeKeys.includes(c.key) && consents[c.key])
  // One confirmation for the whole document: ticking it gives every consent (template [consent-all: …])
  const consentAll = request?.consentAll ?? null
  const allConsented = consentAll
    ? docConsents.length > 0 && docConsents.every(c => consents[c.key])
    : docConsents.length > 0
      ? docConsents.every(c => c.optional || consents[c.key]) && (!isWaiver || purposeGiven)
      : !!(consents.read_understood && consents.privacy)
  const representative = isWaiver && request?.signer && request.signer.role !== 'patient' ? request.signer : null
  const smsPhoneMasked = request?.signer?.phoneMasked ?? request?.patient.phoneMasked ?? ''
  const privacyUrl = tenant?.privacyUrl

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      <main className="flex-1 flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-card shadow-sm p-6 sm:p-8 max-w-2xl w-full">
          <div className="flex justify-center mb-6"><Logo size={100} /></div>

          {fromRelatedPersonForm && (phase === 'read' || phase === 'otp' || phase === 'signed') && (
            <StepIndicator
              steps={[t('informedConsent.flow.stepOne'), t('informedConsent.flow.stepTwo')]}
              current={phase === 'signed' ? 2 : 1}
              caption={t('informedConsent.flow.caption')}
            />
          )}

          {request?.sandbox && (
            <div className="mb-4 rounded-card border border-amber-300 bg-amber-50 text-amber-900 text-small px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{t('informedConsent.sandboxNotice')}</span>
            </div>
          )}

          {phase === 'loading' && (
            <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading')}
            </div>
          )}

          {(phase === 'invalid' || phase === 'expired' || phase === 'canceled') && (
            <div className="text-center py-4">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h1 className="text-h2 text-foreground mb-2">{t(`informedConsent.${phase}.title`)}</h1>
              <p className="text-body text-muted-foreground">{t(`informedConsent.${phase}.body`)}</p>
              {tenant?.contactEmail && (
                <p className="text-small text-muted-foreground mt-4">
                  {t('informedConsent.contact')} <a className="underline" href={`mailto:${tenant.contactEmail}`}>{tenant.contactEmail}</a>
                </p>
              )}
            </div>
          )}

          {(phase === 'read' || phase === 'otp') && request && (
            <>
              <h1 className="text-h2 text-foreground mb-1 text-center">{kindTitle}</h1>
              <p className="text-body text-muted-foreground mb-5 text-center">
                {representative
                  ? t('informedConsent.waiver.greetingRepresentative', { name: `${representative.firstName} ${representative.lastName}`, patient: `${request.patient.firstName} ${request.patient.lastName}` })
                  : t('informedConsent.greeting', { name: `${request.patient.firstName} ${request.patient.lastName}` })}
              </p>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-small mb-5 bg-muted/50 rounded-card p-4">
                <div><dt className="text-muted-foreground">{t('informedConsent.patientNumber')}</dt><dd className="font-medium">{request.patient.patientNumber}</dd></div>
                <div><dt className="text-muted-foreground">{t('informedConsent.document')}</dt><dd className="font-medium">{request.templateName}</dd></div>
                {request.medication && (
                  <div><dt className="text-muted-foreground">{t('informedConsent.medication')}</dt><dd className="font-medium">{request.medication}</dd></div>
                )}
                {request.dosage && (
                  <div><dt className="text-muted-foreground">{t('informedConsent.dosage')}</dt><dd className="font-medium">{request.dosage}</dd></div>
                )}
                {request.relatedPerson && (
                  <div><dt className="text-muted-foreground">{t('informedConsent.waiver.relatedPerson')}</dt>
                    <dd className="font-medium">{request.relatedPerson.firstName} {request.relatedPerson.lastName} <span className="font-normal text-muted-foreground">({request.relatedPerson.relationship})</span></dd></div>
                )}
                {representative && (
                  <div><dt className="text-muted-foreground">{t('informedConsent.waiver.signer')}</dt>
                    <dd className="font-medium">{representative.firstName} {representative.lastName}</dd></div>
                )}
                {request.colleague && (
                  <div className="sm:col-span-2"><dt className="text-muted-foreground">{t(`informedConsent.role.${request.colleague.role}`)}</dt>
                    <dd className="font-medium">{[request.colleague.title, request.colleague.name].filter(Boolean).join(' ')}</dd></div>
                )}
              </dl>

              {/* Document preview */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-small font-medium text-foreground flex items-center gap-1.5"><FileText className="w-4 h-4" />{t('common.previewLabel')}</span>
                  {pdfUrl && (
                    <button type="button" onClick={() => window.open(`${pdfUrl}#navpanes=0&pagemode=none`, '_blank', 'noopener')} className="text-small text-primary underline flex items-center gap-1">
                      <ExternalLink className="w-3.5 h-3.5" /> {t('common.openInNewTab')}
                    </button>
                  )}
                </div>
                {pdfUrl && isMobile ? (
                  <div className="border border-border rounded-card p-4 bg-muted/40 flex flex-col items-center gap-3 text-center">
                    <FileText className="w-8 h-8 text-primary" />
                    <p className="text-small text-muted-foreground">{t('informedConsent.mobileOpenHint')}</p>
                    <Button type="button" variant="secondary" onClick={() => window.open(`${pdfUrl}#navpanes=0&pagemode=none`, '_blank', 'noopener')} className="w-full sm:w-auto inline-flex items-center justify-center gap-2">
                      <ExternalLink className="w-4 h-4" /> {t('common.openDocument')}
                    </Button>
                  </div>
                ) : pdfUrl ? (
                  /* PDF open parameters: no navigation pane / toolbar, page fitted to the frame width */
                  <iframe title={t('common.previewLabel')} src={`${pdfUrl}#navpanes=0&toolbar=0&view=FitH&pagemode=none`} className="w-full h-[420px] sm:h-[560px] border border-border rounded-card bg-white" />
                ) : (
                  <div className="h-40 border border-dashed border-border rounded-card flex items-center justify-center text-small text-muted-foreground">
                    {pdfLoading ? t('common.loading') : t('common.previewUnavailable')}
                  </div>
                )}
              </div>

              {phase === 'read' && (
                <div className="space-y-4">
                  {consentAll ? (
                    <>
                      <p className="text-small text-foreground">{t('informedConsent.includesAll')}</p>
                      <ul className="space-y-2">
                        {docConsents.map(c => (
                          <li key={c.key} className="text-small text-foreground flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /><span>{c.text}</span>
                          </li>
                        ))}
                      </ul>
                      <label className="flex items-start gap-3 cursor-pointer font-medium text-small text-foreground border border-border rounded-card p-3">
                        <input type="checkbox" className="mt-0.5" checked={allConsented}
                          onChange={e => setConsents(Object.fromEntries(docConsents.map(c => [c.key, e.target.checked])))} />
                        <span>{consentAll} <span className="text-destructive">*</span></span>
                      </label>
                    </>
                  ) : <>
                  {isWaiver && <p className="text-small text-foreground">{t('informedConsent.waiver.choose')}</p>}
                  {docConsents.length > 0 ? docConsents.map(c => (
                    <div key={c.key} className="text-small text-foreground">
                      {isWaiver && requested.has(c.key) && (
                        <span className="inline-block mb-1 text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded">{t('informedConsent.waiver.requested')}</span>
                      )}
                      <p className="mb-1.5">{c.text}</p>
                      <label className="flex items-start gap-3 cursor-pointer font-medium">
                        <input type="checkbox" className="mt-0.5" checked={!!consents[c.key]}
                          onChange={e => setConsents(prev => ({ ...prev, [c.key]: e.target.checked }))} />
                        {isWaiver
                          ? <span>{t('informedConsent.waiver.release')}</span>
                          : <span>{c.label ?? t('informedConsent.consentBox')} {c.optional ? <span className="text-muted-foreground font-normal">({t('informedConsent.optional')})</span> : <span className="text-destructive">*</span>}</span>}
                      </label>
                      {c.optional && /oppon/i.test(c.label ?? '') && (
                        <p className="text-xs text-muted-foreground mt-1 ml-7">{t('informedConsent.oppositionHint')}</p>
                      )}
                    </div>
                  )) : (<>
                  <label className="flex items-start gap-3 text-small text-foreground cursor-pointer">
                    <input type="checkbox" className="mt-0.5" checked={!!consents.read_understood}
                      onChange={e => setConsents(c => ({ ...c, read_understood: e.target.checked }))} />
                    <span>{t('informedConsent.consentRead')} <span className="text-destructive">*</span></span>
                  </label>
                  <label className="flex items-start gap-3 text-small text-foreground cursor-pointer">
                    <input type="checkbox" className="mt-0.5" checked={!!consents.privacy}
                      onChange={e => setConsents(c => ({ ...c, privacy: e.target.checked }))} />
                    <span>
                      {t('informedConsent.consentPrivacyBefore')}{' '}
                      {privacyUrl ? <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="underline">{t('common.datenschutz')}</a> : t('common.datenschutz')}{' '}
                      {t('informedConsent.consentPrivacyAfter')} <span className="text-destructive">*</span>
                    </span>
                  </label>
                  </>)}
                  </>}

                  {isWaiver && !consentAll && !purposeGiven && <p className="text-small text-muted-foreground">{t('informedConsent.waiver.purposeRequired')}</p>}
                  <Button onClick={() => sendOtp(false)} disabled={!allConsented || busy} className="w-full">
                    {busy ? t('common.sending') : t('informedConsent.sendCode')}
                  </Button>
                  <p className="text-small text-muted-foreground flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{t('informedConsent.otpHint', { phone: smsPhoneMasked })}</span>
                  </p>
                  <p className="text-small text-muted-foreground">
                    {t('informedConsent.wrongNumber')}{tenant?.contactEmail && <> <a className="underline" href={`mailto:${tenant.contactEmail}`}>{tenant.contactEmail}</a></>}
                  </p>
                </div>
              )}

              {phase === 'otp' && (
                <form onSubmit={e => { e.preventDefault(); void sign() }} className="space-y-4">
                  <p className="text-body text-foreground text-center">{t('informedConsent.enterCode', { phone: phoneMasked || smsPhoneMasked })}</p>
                  <div className="flex justify-center gap-2" onPaste={onOtpPaste}>
                    {code.map((d, i) => (
                      <input
                        key={i}
                        ref={el => { otpRefs.current[i] = el }}
                        inputMode="numeric"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        maxLength={1}
                        value={d}
                        onChange={e => onOtpChange(i, e.target.value)}
                        onKeyDown={e => onOtpKeyDown(i, e)}
                        className="w-11 h-12 text-center text-lg border border-border rounded-input focus:outline-none focus:ring-2 focus:ring-primary"
                        aria-label={`${t('informedConsent.digit')} ${i + 1}`}
                      />
                    ))}
                  </div>
                  <Button type="submit" disabled={code.join('').length !== OTP_LENGTH || busy} className="w-full">
                    {busy ? t('common.loading') : t('informedConsent.signNow')}
                  </Button>
                  <p className="text-small text-muted-foreground text-center">
                    {t('step10.noCodeReceived')}{' '}
                    <button type="button" disabled={cooldown > 0 || busy} onClick={() => sendOtp(true)} className="underline disabled:no-underline disabled:opacity-60">
                      {cooldown > 0 ? t('step10.resendIn', { s: cooldown }) : t('step10.resend')}
                    </button>
                  </p>
                </form>
              )}
            </>
          )}

          {phase === 'signed' && request && (
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h1 className="text-h2 text-foreground mb-2">{t('informedConsent.signed.title')}</h1>
              {fromRelatedPersonForm && <p className="text-body text-foreground mb-2">{t('informedConsent.flow.allDone')}</p>}
              <p className="text-body text-muted-foreground mb-6">
                {request.stored ? t('informedConsent.signed.stored') : t('informedConsent.signed.preparing')}
              </p>
              {request.stored && pdfUrl && signedPdfReady ? (
                <Button variant="download" onClick={download} className="w-full sm:w-auto inline-flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> {t('informedConsent.signed.download')}
                </Button>
              ) : (
                copyFollowsLater ? (
                  <p className="text-small text-muted-foreground">{t('informedConsent.signed.later')}</p>
                ) : (
                  <div className="text-small text-muted-foreground flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading')}
                  </div>
                )
              )}
            </div>
          )}

          {phase === 'error' && (
            <p className="text-body text-muted-foreground text-center">{t('common.serverError')}</p>
          )}
        </div>
      </main>
      <LegalFooter />
    </div>
  )
}
